import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { useMailOrbitEnabled } from 'src/utils/mailorbitFeature';
import { getNotes, addNote, getMailOrbitErrorKey } from 'src/utils/MailOrbitService';
import { formatDateTime } from 'src/components/MailOrbit/WorkOrderEmail/format';

const NOTE_MAX = 4000;

const byNewest = (a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime();

/**
 * İç notlar: liste (en yeni üstte) ve ekleme. Notlar müşteriye gitmez.
 * Kapı kapalıyken hiçbir şey çizmez ve istek atmaz.
 */
const InternalNotes = ({ conversationId, canWork }) => {
  const enabled = useMailOrbitEnabled();
  const { t } = useTranslation(['mailorbitActions', 'mailorbit']);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    setNotes([]);
    getNotes(conversationId)
      .then(list => {
        if (!cancelled) setNotes((Array.isArray(list) ? list : []).slice().sort(byNewest));
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, conversationId]);

  if (!enabled) return null;

  const body = text.trim();

  const onAdd = async () => {
    if (!body || saving) return;
    const tempId = `pending-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body,
      authorDisplayName: '',
      createdAtUtc: new Date().toISOString(),
      pending: true
    };
    setSaving(true);
    setError(null);
    setNotes(prev => [optimistic, ...prev]);
    setText('');
    try {
      const saved = await addNote(conversationId, body);
      setNotes(prev => prev.map(n => (n.id === tempId ? { ...optimistic, ...saved, body, pending: false } : n)));
    } catch (e) {
      // geri al: not listeden çıkar, metin kutusuna geri konur
      setNotes(prev => prev.filter(n => n.id !== tempId));
      setText(body);
      setError(t(`mailorbit:${getMailOrbitErrorKey(e)}`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box style={{ padding: '12px 14px', borderBottom: '1px solid #e2e7ea' }}>
      {canWork && (
        <Box style={{ marginBottom: 12 }}>
          <TextField
            fullWidth
            multiline
            rows={focused || text ? 4 : 2}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            variant="outlined"
            size="small"
            placeholder={t('mailorbitActions:note_placeholder')}
            value={text}
            onChange={e => setText(e.target.value)}
            inputProps={{ maxLength: NOTE_MAX }}
            disabled={saving}
            helperText={
              text.length > 0
                ? `${t('mailorbitActions:notes_warning')} ${t('mailorbitActions:note_limit', {
                    count: text.length,
                    max: NOTE_MAX
                  })}`
                : t('mailorbitActions:notes_warning')
            }
          />
          {error && (
            <Alert severity="error" onClose={() => setError(null)} style={{ marginTop: 8 }}>
              {error}
            </Alert>
          )}
          <Button
            size="small"
            variant="contained"
            color="primary"
            style={{ marginTop: 8 }}
            disabled={!body || saving}
            onClick={onAdd}
          >
            {t('mailorbitActions:note_add')}
          </Button>
        </Box>
      )}
      {loading && <CircularProgress size={20} />}
      {!loading && loadFailed && (
        <Alert severity="error">{t('mailorbitActions:notes_loading_error')}</Alert>
      )}
      {!loading && !loadFailed && notes.length === 0 && (
        <Typography variant="caption" color="textSecondary">
          {t('mailorbitActions:notes_empty')}
        </Typography>
      )}
      {notes.map(n => (
        <Box
          key={n.id}
          style={{ padding: '8px 0', borderTop: '1px solid #e2e7ea', opacity: n.pending ? 0.6 : 1 }}
        >
          <Typography variant="caption" color="textSecondary" component="div">
            {n.authorDisplayName} {formatDateTime(n.createdAtUtc)}
          </Typography>
          <Typography style={{ fontSize: 13, color: '#263238', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {n.body}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

InternalNotes.propTypes = {
  conversationId: PropTypes.string.isRequired,
  canWork: PropTypes.bool
};

export default InternalNotes;
